import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  SearchableSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@jem-open/jem-ui';

import Swal from 'sweetalert2';
import moment from 'moment';
import axios from 'axios';

import apiResponseHandler from '../../../utils/apiResponseHandler';
import { environment } from '../../../environment/environment';
import { localStorageService } from '../../../services';

/**
 * @summary AddNewAdvanceBatchModal component
 *
 * @param {Props} props
 * @component
 */

const getToday = (dateOnly = false) => {
  const d = new Date();
  return dateOnly ? new Date(d).toDateString() : d.toISOString().slice(0, 10);
};

const checkIfEndDateInTheFuture = ({ selectedDay }) => {
  const dayDate = new Date(selectedDay);
  const today = new Date();
  if (today.getDate() < dayDate.getDate()) {
    return false;
  }
  return true;
};

const checkIfEndTimeInTheFuture = ({ selectedDay, selectedTime }) => {
  const dayDate = new Date(selectedDay);
  const today = new Date();
  if (today.getDate() > dayDate.getDate()) {
    return true;
  }
  const timeSelectedDay = new Date(`${selectedDay} ${selectedTime}`);

  if (today < timeSelectedDay) {
    return false;
  }
  if (today > timeSelectedDay) {
    return true;
  }
  return true;
};

const AddNewAdvanceBatchModal = (props) => {
  const {
    toggleAddNewBatchModal,
    handleShowAddNewBatchModal,
    getAdvanceBatches,
    activeEmployers,
    isLoadingActiveEmployers,
  } = props;

  // Local copy of employer options so searches are isolated to this modal instance
  const employerOptions = useMemo(
    () =>
      (activeEmployers || []).map((employer) => ({
        value: String(employer.id),
        label: employer.company_name,
        employer,
      })),
    [activeEmployers]
  );

  const [state, setState] = useState({
    selectedEmployerOption: null,
    activeEmployer: null,
    newAdvanceBatchPayload: {},
    lastPayrollBatch: null,
    selectedPayrollId: null,
  });

  const [trySubmit, setTriedSubmitting] = useState(false);

  const handlePayrollChange = (selectedPayrollId) => {
    axios
      .get(`${environment.baseUrl}/advance_batch/payroll/${selectedPayrollId}/`)
      .then((res) => {
        const lastPayrollBatch = res.data;
        // When the payroll has a previous batch, prefill the start fields from
        // its end date/time using formats the native inputs accept
        // (date: YYYY-MM-DD, time: HH:mm).
        const prefill =
          lastPayrollBatch?.end_datetime
            ? {
                startDate: moment(lastPayrollBatch.end_datetime).format(
                  'YYYY-MM-DD'
                ),
                startDateTime: moment(lastPayrollBatch.end_datetime).format(
                  'HH:mm'
                ),
              }
            : {};
        setState((prev) => ({
          ...prev,
          lastPayrollBatch,
          loading: false,
          selectedPayrollId,
          newAdvanceBatchPayload: {
            ...prev.newAdvanceBatchPayload,
            // Clear any stale start fields before overlaying the new
            // selection's prefill (empty when it has no previous batch).
            startDate: '',
            startDateTime: '',
            ...prefill,
          },
        }));
      });
  };

  const handleEmployerChange = (value) => {
    const option = employerOptions.find((o) => o.value === value) || null;
    setState((prev) => ({
      ...prev,
      selectedEmployerOption: option,
      activeEmployer: option ? option.employer : null,
      lastPayrollBatch: null,
      selectedPayrollId: null,
      // Changing employer invalidates the payroll selection, so drop any
      // start fields prefilled from the previous payroll.
      newAdvanceBatchPayload: {
        ...prev.newAdvanceBatchPayload,
        startDate: '',
        startDateTime: '',
      },
    }));
  };

  const handleInputChange = (event) => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;

    setState((prev) => ({
      ...prev,
      newAdvanceBatchPayload: {
        ...prev.newAdvanceBatchPayload,
        [name]: value,
      },
    }));
  };

  const showValidation = (title) => {
    Swal.fire({
      title,
      icon: 'warning',
      toast: true,
      position: 'top',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };

  const handleSubmit = () => {
    setTriedSubmitting(true);
    const { user } = localStorageService;
    const currentUserId = user?.id;
    const {
      lastPayrollBatch,
      newAdvanceBatchPayload,
      activeEmployer,
      selectedPayrollId,
    } = state;

    // Guard against an incomplete form so we never post an "Invalid date".
    if (!activeEmployer) {
      showValidation('Please select an employer.');
      return;
    }
    if (!selectedPayrollId) {
      showValidation('Please select a payroll.');
      return;
    }

    const startMoment = lastPayrollBatch
      ? moment(`${lastPayrollBatch.end_datetime}`).utc()
      : moment(
          `${newAdvanceBatchPayload.startDate} ${newAdvanceBatchPayload.startDateTime}`
        );
    const endMoment = moment(
      `${newAdvanceBatchPayload.endDate} ${newAdvanceBatchPayload.endDateTime}`
    );

    if (!startMoment.isValid()) {
      showValidation('Please provide a valid start date and time.');
      return;
    }
    if (!endMoment.isValid()) {
      showValidation('Please provide a valid end date and time.');
      return;
    }

    const payload = {
      /** set start date of new batch to the end date of the last batch */
      start_date: startMoment.format(),
      end_date: endMoment.format(),
      employer: activeEmployer.id,
      payroll: selectedPayrollId,
      requested_by: currentUserId,
    };
    const url = `${environment.baseUrl}/advance_batch_job/`;

    if (
      !(
        state.newAdvanceBatchPayload.endDateTime &&
        state.newAdvanceBatchPayload.endDate &&
        checkIfEndTimeInTheFuture({
          selectedDay: state.newAdvanceBatchPayload.endDate,
          selectedTime: state.newAdvanceBatchPayload.endDateTime,
        })
      )
    ) {
      Swal.fire({
        title: 'End Date and time can not be in the future.',
        // text: 'Please change the end date and time.',
        icon: 'warning',
        toast: true,
        position: 'top',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }
    axios
      .post(url, payload)
      .then(() => {
        Swal.fire({
          title: 'Advance Batch Job successfully submitted.',
          icon: 'success',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 10000,
          timerProgressBar: true,
        });
        handleShowAddNewBatchModal();
        getAdvanceBatches();
      })
      .catch((error) => {
        apiResponseHandler.handleApiError(
          'Error submitting Advance Batch Job',
          error
        );
        setState({ ...state, containerState: 'STATE_ERROR' });
      });
  };

  const startDateReadOnly =
    !!state.lastPayrollBatch && state.lastPayrollBatch.last_batch_id;

  const endDateInvalid =
    trySubmit &&
    !(
      state.newAdvanceBatchPayload.endDate &&
      checkIfEndDateInTheFuture({
        selectedDay: state.newAdvanceBatchPayload.endDate,
      })
    );

  const endTimeInvalid =
    trySubmit &&
    !(
      state.newAdvanceBatchPayload.endDateTime &&
      state.newAdvanceBatchPayload.endDate &&
      checkIfEndTimeInTheFuture({
        selectedDay: state.newAdvanceBatchPayload.endDate,
        selectedTime: state.newAdvanceBatchPayload.endDateTime,
      })
    );

  return (
    <Dialog
      open={toggleAddNewBatchModal}
      onOpenChange={(next) => {
        if (!next) handleShowAddNewBatchModal();
      }}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Generate New Advance Batch</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-md overflow-y-auto">
          <div className="flex flex-col gap-xxs">
            <Label htmlFor="employerSelect">Employer</Label>
            <SearchableSelect
              options={employerOptions}
              value={
                state.selectedEmployerOption
                  ? state.selectedEmployerOption.value
                  : ''
              }
              placeholder="Search employer..."
              searchPlaceholder="Search employer..."
              emptyMessage={
                isLoadingActiveEmployers
                  ? 'Loading employers...'
                  : 'No employers found.'
              }
              onValueChange={handleEmployerChange}
            />
          </div>

          <div className="flex flex-col gap-xxs">
            <Label htmlFor="selectedPayroll">Payroll</Label>
            <Select
              value={state.selectedPayrollId ? String(state.selectedPayrollId) : ''}
              onValueChange={handlePayrollChange}
              disabled={!state.activeEmployer}>
              <SelectTrigger id="selectedPayroll">
                <SelectValue placeholder="Select payroll" />
              </SelectTrigger>
              <SelectContent>
                {!!state.activeEmployer &&
                  state.activeEmployer.pay_cycles &&
                  state.activeEmployer.pay_cycles.map((payCycle) => (
                    <SelectItem key={payCycle.id} value={String(payCycle.id)}>
                      {payCycle.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-xxs">
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              type="date"
              name="startDate"
              id="startDate"
              placeholder="Batch Start Date"
              onChange={handleInputChange}
              required
              readOnly={startDateReadOnly}
              value={state.newAdvanceBatchPayload.startDate || ''}
            />
          </div>

          <div className="flex flex-col gap-xxs">
            <Label htmlFor="startDateTime">Start Date Time</Label>
            <Input
              type="time"
              name="startDateTime"
              id="startDateTime"
              onChange={handleInputChange}
              readOnly={startDateReadOnly}
              value={state.newAdvanceBatchPayload.startDateTime || ''}
            />
          </div>

          <div className="flex flex-col gap-xxs">
            <Label htmlFor="endDate">End Date</Label>
            <Input
              type="date"
              name="endDate"
              id="endDate"
              max={getToday()}
              onChange={handleInputChange}
              aria-invalid={endDateInvalid}
              value={state.newAdvanceBatchPayload.endDate || ''}
            />
          </div>

          <div className="flex flex-col gap-xxs">
            <Label htmlFor="endDateTime">End Date Time</Label>
            <Input
              type="time"
              name="endDateTime"
              id="endDateTime"
              onChange={handleInputChange}
              aria-invalid={endTimeInvalid}
              value={state.newAdvanceBatchPayload.endDateTime || ''}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleShowAddNewBatchModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

AddNewAdvanceBatchModal.defaultProps = {
  activeEmployers: [],
  isLoadingActiveEmployers: false,
};

AddNewAdvanceBatchModal.propTypes = {
  toggleAddNewBatchModal: PropTypes.oneOfType([PropTypes.bool, PropTypes.func])
    .isRequired,
  handleShowAddNewBatchModal: PropTypes.func.isRequired,
  getAdvanceBatches: PropTypes.func.isRequired,
  activeEmployers: PropTypes.arrayOf(PropTypes.shape({})),
  isLoadingActiveEmployers: PropTypes.bool,
};

export default AddNewAdvanceBatchModal;
